import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { AppStoreIconBadge, GooglePlayIconBadge } from "@/components/AppBadges";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

type NavLink = { label: string; to?: string; href?: string };

const productLinks: NavLink[] = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Integrations", to: "/integrations" },
  { label: "Industries", to: "/industries" },
  { label: "Videos", to: "/videos" },
  { label: "API docs", to: "/api-docs" },
];

const companyLinks: NavLink[] = [
  { label: "About", to: "/about" },
  { label: "Customers", to: "/customers" },
  { label: "Contact", href: "https://help.freecrm.com/hc/en-us/requests/new" },
];

const Navbar = () => {
  const { pathname } = useLocation();
  const linkClass = (to: string) =>
    `text-sm font-medium transition-colors hover:text-secondary ${
      pathname === to ? "text-secondary" : "text-muted-foreground"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl font-bold text-secondary">
          <img src="/favicon.png" alt="FreeCRM logo" className="h-8 w-8 object-contain" />
          FreeCRM
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          {productLinks.map((l) => (
            <Link key={l.to} to={l.to} className={linkClass(l.to)}>
              {l.label}
            </Link>
          ))}
          <Link to="/about" className={linkClass("/about")}>
            About
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-1.5 lg:flex">
            <AppStoreIconBadge />
            <GooglePlayIconBadge />
          </div>
          <Button variant="ghost" className="hidden sm:inline-flex" asChild>
            <a href="https://ui.freecrm.com/" target="_blank" rel="noopener noreferrer">
              Log in
            </a>
          </Button>
          <Button variant="hero" asChild>
            <a href="https://api.freecrm.com/register/?lang=en" target="_blank" rel="noopener noreferrer">
              Get FreeCRM
            </a>
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="mt-8 flex flex-col gap-1">
                {[...productLinks, ...companyLinks].map((l) =>
                  l.href ? (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md px-3 py-2 text-base font-medium text-secondary hover:bg-muted"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="rounded-md px-3 py-2 text-base font-medium text-secondary hover:bg-muted"
                    >
                      {l.label}
                    </Link>
                  )
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
